import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { message } from 'antd';
import { Button, Form, Spinner } from 'react-bootstrap';
import { HomeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { feedbackService, FeedbackPost, FeedbackCategory } from '../../services/feedback/feedbackService';
import { useAuth } from '../../contexts/AuthContext';
import { paginateDemoFeedbackPosts } from '../../demo/demoBrowseMocks';
import { LimsNavIcon } from '../../components/LimsNavIcon';
import '../MemberManagement.css';
import '../../styles/resourceManagement.css';
import { feedbackCategoryPill, feedbackStatusPill, feedbackFeedTabKey } from './feedbackUi';

const categoryOptions: { value: FeedbackCategory; label: string }[] = [
  { value: '建议', label: '建议' },
  { value: '吐槽/反馈', label: '吐槽 / 反馈' },
  { value: '问题举报', label: '问题举报' },
  { value: '求助', label: '求助' },
  { value: '投票讨论', label: '投票讨论' },
  { value: '表扬', label: '表扬' },
];

const feedTabs = [
  { key: 'hot', label: '热门' },
  { key: 'new', label: '最新' },
  { key: 'pending', label: '待处理' },
  { key: 'resolved', label: '已解决' },
] as const;

const FeedbackSquare: React.FC = () => {
  const [posts, setPosts] = useState<FeedbackPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [category, setCategory] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [sort, setSort] = useState<'hot' | 'new' | 'pending'>('hot');
  const navigate = useNavigate();
  const { role, isDemoBrowseOnly } = useAuth();

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      if (isDemoBrowseOnly) {
        const result = paginateDemoFeedbackPosts({
          page,
          pageSize,
          category: category || undefined,
          status: statusFilter,
          sort
        });
        setPosts(result.items);
        setTotal(result.total);
        return;
      }
      const result = await feedbackService.listPosts({
        page,
        pageSize,
        category: category || undefined,
        status: statusFilter,
        sort,
      });
      setPosts(result.items);
      setTotal(result.total);
    } catch (error: any) {
      message.error(error.message || '获取匿名反馈列表失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, category, statusFilter, sort, isDemoBrowseOnly]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleTabChange = (key: string) => {
    if (key === 'hot') {
      setSort('hot');
      setStatusFilter(undefined);
    } else if (key === 'new') {
      setSort('new');
      setStatusFilter(undefined);
    } else if (key === 'pending') {
      setSort('pending');
      setStatusFilter('pending');
    } else if (key === 'resolved') {
      setSort('new');
      setStatusFilter('resolved');
    }
    setPage(1);
  };

  const activeFeedKey = feedbackFeedTabKey(sort, statusFilter);

  const stats = useMemo(() => {
    const pagePending = posts.filter((p) => p.status === 'pending').length;
    const pageLikes = posts.reduce((s, p) => s + (p.likeCount || 0), 0);
    return { total, pagePending, pageLikes };
  }, [posts, total]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);

  return (
    <div className="page-container full-height members-lims fb-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <span>畅所欲言</span>
      </nav>

      <header className="mm-page-head">
        <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 w-100">
          <div>
            <h1 className="mm-title">畅所欲言</h1>
            <p className="mm-subtitle mb-0">
              匿名建议与讨论空间。对普通成员匿名展示；媒体上传将清理元数据。管理员可从侧栏进入审核与配置。
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2 justify-content-end">
            {role === '超级管理员' && (
              <>
                <Button variant="outline-secondary" size="sm" className="rounded-pill" onClick={() => navigate('/admin/feedback/review')}>
                  审核中心
                </Button>
                <Button variant="outline-secondary" size="sm" className="rounded-pill" onClick={() => navigate('/admin/feedback/analytics')}>
                  数据看板
                </Button>
                <Button variant="outline-secondary" size="sm" className="rounded-pill" onClick={() => navigate('/admin/feedback/settings')}>
                  敏感词
                </Button>
              </>
            )}
            <button type="button" className="mm-btn-primary" onClick={() => navigate('/feedback/post/new')}>
              <LimsNavIcon name="add_comment" sizePx={20} />
              发表匿名反馈
            </button>
          </div>
        </div>
      </header>

      <section className="mm-stats mm-stats--quad" aria-label="广场概览">
        <article className="mm-stat">
          <span className="mm-stat-label">符合条件的帖子</span>
          <span className="mm-stat-value" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {stats.total}
          </span>
          <div className="mm-stat-foot">
            <LimsNavIcon name="forum" sizePx={18} />
            <span>当前筛选下服务端总数</span>
          </div>
          <LimsNavIcon name="chat" sizePx={120} className="mm-stat-deco" />
        </article>
        <article className="mm-stat">
          <span className="mm-stat-label">本页条数</span>
          <span className="mm-stat-value">{posts.length}</span>
          <div className="mm-stat-foot">
            <LimsNavIcon name="view_list" sizePx={18} />
            <span>当前页展示</span>
          </div>
          <LimsNavIcon name="format_list_bulleted" sizePx={120} className="mm-stat-deco" />
        </article>
        <article className="mm-stat">
          <span className="mm-stat-label">本页待处理</span>
          <span className={`mm-stat-value${stats.pagePending > 0 ? ' mm-stat-value--alert' : ''}`}>{stats.pagePending}</span>
          <div className="mm-stat-foot">
            <LimsNavIcon name="schedule" sizePx={18} />
            <span>状态为待处理（本页）</span>
          </div>
          <LimsNavIcon name="pending_actions" sizePx={120} className="mm-stat-deco" />
        </article>
        <article className="mm-stat">
          <span className="mm-stat-label">本页赞同数</span>
          <span className="mm-stat-value" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {stats.pageLikes}
          </span>
          <div className="mm-stat-foot">
            <LimsNavIcon name="thumb_up" sizePx={18} />
            <span>当前页点赞合计</span>
          </div>
          <LimsNavIcon name="social_leaderboard" sizePx={120} className="mm-stat-deco" />
        </article>
      </section>

      <div className="mm-table-shell">
        <div className="mm-table-toolbar flex-column align-items-stretch">
          <div className="d-flex flex-wrap w-100 align-items-center justify-content-between gap-3 mb-2">
            <div className="mm-filter-row flex-grow-1 flex-wrap">
              <span className="mm-filter-label">浏览：</span>
              <div className="mm-filter-pills">
                {feedTabs.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    className={`mm-pill ${activeFeedKey === t.key ? 'is-active' : ''}`}
                    onClick={() => handleTabChange(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <Form.Select
              size="sm"
              style={{ minWidth: 180, maxWidth: 220 }}
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setPage(1);
              }}
              aria-label="按类型筛选"
            >
              <option value="">全部类型</option>
              {categoryOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="mm-toolbar-meta flex-wrap">
            <span>
              共 <strong>{total}</strong> 条 · 第 {page} / {totalPages} 页
            </span>
          </div>
        </div>

        <div className="mm-sheet-scroll fb-feed-scroll px-2">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="sm" className="me-2" />
              加载中…
            </div>
          ) : posts.length === 0 ? (
            <div className="mm-empty text-center py-5">暂无帖子，欢迎发表第一条匿名反馈</div>
          ) : (
            posts.map((item) => (
              <div
                key={item._id}
                className="fb-feed-row"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/feedback/post/${item._id}`)}
                onKeyDown={(e) => e.key === 'Enter' && navigate(`/feedback/post/${item._id}`)}
              >
                <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                  {feedbackCategoryPill(item.category)}
                  {feedbackStatusPill(item.status)}
                </div>
                <h2 className="fb-feed-title">{item.title}</h2>
                <p className="fb-feed-excerpt">{item.content}</p>
                <div className="fb-feed-meta">
                  <span>匿名 · {new Date(item.createdAt).toLocaleString('zh-CN')}</span>
                  <span>
                    {item.likeCount} 赞同 · {item.disagreeCount} 不认同 · {item.commentCount} 条评论
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        <div
          className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-2 py-3 border-top"
          style={{ borderColor: 'rgba(0, 53, 95, 0.08)' }}
        >
          <Form.Select
            size="sm"
            style={{ width: 'auto' }}
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            aria-label="每页条数"
          >
            {[10, 20, 50].map((n) => (
              <option key={n} value={n}>
                每页 {n} 条
              </option>
            ))}
          </Form.Select>
          <div className="d-flex align-items-center gap-2">
            <Button variant="outline-secondary" size="sm" className="rounded-pill" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>
              上一页
            </Button>
            <span className="small text-muted px-2">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline-secondary"
              size="sm"
              className="rounded-pill"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              下一页
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackSquare;
